import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import xss from 'xss'

import { Checkbox } from '@wartek-id/checkbox-v1'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  CollapsibleIcon,
} from '@wartek-id/collapsible-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Input } from '@wartek-id/input'
import { Text } from '@wartek-id/text-v1'

import usePrevious from 'utils/hooks/usePrevious'

import type { KinerjaHarianFormProps } from 'types/kinerja-harian'

type KinerjaLogOptionProps = {
  id: string
  label: string
  descriptionHtml?: string
}

// convert time string to minutes for easier comparison
const toMinutes = (timeStr: string) => {
  const [hours, minutes] = timeStr.split(':').map(Number)
  return hours * 60 + minutes
}

export function KinerjaLogOption({
  id,
  label,
  descriptionHtml,
}: KinerjaLogOptionProps) {
  const { register, watch, formState, trigger } =
    useFormContext<KinerjaHarianFormProps>()
  const [isOpen, setIsOpen] = useState(false)

  const startTimeInputId = `kinerja-log-option-start-time-${id}`
  const endTimeInputId = `kinerja-log-option-end-time-${id}`

  const values = watch(`contents.${id}`)

  const previousIsChecked = usePrevious(values?.isChecked)

  useEffect(() => {
    if (values?.isChecked && !previousIsChecked) {
      setIsOpen(true)
    } else if (!values?.isChecked && previousIsChecked) {
      setIsOpen(false)

      // Clear validation errors when the option is unchecked
      trigger([`contents.${id}.startTime`, `contents.${id}.endTime`])
    }
  }, [values?.isChecked, previousIsChecked, trigger, id])

  // Cross-validate endTime when startTime changes
  useEffect(() => {
    if (values?.startTime) {
      trigger(`contents.${id}.endTime`)
    }
  }, [values?.startTime, id, trigger])

  // Cross-validate startTime when endTime changes
  useEffect(() => {
    if (values?.endTime) {
      trigger(`contents.${id}.startTime`)
    }
  }, [values?.endTime, id, trigger])

  return (
    <Collapsible
      className="border-b border-subdued last:border-0"
      open={isOpen}
      onToggle={setIsOpen}
    >
      <CollapsibleTrigger
        as="div"
        className="flex justify-between items-center p-6 gap-2 hover:bg-neutral-50 cursor-pointer group"
      >
        <div className="flex-1" onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={values?.isChecked}
            className="!items-center"
            {...register(`contents.${id}.isChecked`)}
          >
            <Text className="ml-4">{label}</Text>
          </Checkbox>
        </div>
        <div className="text-subdued pt-1 group-hover:text-primary-600">
          <CollapsibleIcon />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="px-6 pb-5 bg-gray-0">
          {descriptionHtml && (
            <div
              className="py-4 px-8 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-2"
              dangerouslySetInnerHTML={{
                __html: xss(descriptionHtml),
              }}
            />
          )}

          <div className="bg-white border border-subdued rounded-lg p-4 mt-4">
            <div className="flex items-start gap-2 mb-2">
              <Icon aria-hidden>schedule</Icon>
              <div>
                <Text as="h3" variant="heading-sm">
                  Waktu
                </Text>

                <Text color="subdued" className="mt-1">
                  Pastikan total jam dari semua jenis kinerja yang dipilih
                  memenuhi aturan jam kerja ASN: Minimal 37,5 jam/minggu, setara
                  dengan 7,5 jam/hari untuk 5 hari kerja atau 6,25 jam/hari
                  untuk 6 hari kerja.
                </Text>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Text
                  as="label"
                  htmlFor={startTimeInputId}
                  className="block mb-1"
                  variant="body-sm"
                >
                  Jam Mulai
                </Text>
                <Input
                  id={startTimeInputId}
                  type="time"
                  aria-label="Jam Mulai"
                  errorMessage={
                    formState.errors?.contents?.[id]?.startTime?.message
                  }
                  isInvalid={!!formState.errors?.contents?.[id]?.startTime}
                  {...register(`contents.${id}.startTime`, {
                    validate: (value) => {
                      if (!values?.isChecked) {
                        return true // skip validation if not checked
                      }

                      if (!value) {
                        return 'Wajib diisi'
                      }

                      const endTime = values.endTime
                      if (!endTime) {
                        return true // Don't validate comparison if endTime not filled yet
                      }

                      return (
                        toMinutes(value) < toMinutes(endTime) ||
                        'Jam Mulai harus lebih kecil dari Jam Berakhir'
                      )
                    },
                  })}
                />
              </div>

              <div>
                <Text
                  as="label"
                  htmlFor={endTimeInputId}
                  className="block mb-1"
                  variant="body-sm"
                >
                  Jam Berakhir
                </Text>
                <Input
                  id={endTimeInputId}
                  type="time"
                  aria-label="Jam Berakhir"
                  errorMessage={
                    formState.errors?.contents?.[id]?.endTime?.message
                  }
                  isInvalid={!!formState.errors?.contents?.[id]?.endTime}
                  {...register(`contents.${id}.endTime`, {
                    validate: (value) => {
                      if (!values?.isChecked) {
                        return true // skip validation if not checked
                      }

                      if (!value) {
                        return 'Wajib diisi'
                      }

                      const startTime = values.startTime
                      if (!startTime) {
                        return true // Don't validate comparison if startTime not filled yet
                      }

                      return (
                        toMinutes(value) > toMinutes(startTime) ||
                        'Jam Berakhir harus lebih besar dari Jam Mulai'
                      )
                    },
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
